import React, { useEffect, useMemo, useRef, useState } from "react"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { PATH_MAP } from "../../configuration/PathMap"
import { EAccountPlansType } from "../../Enum/EAccountPlansType"
import { EBillingPeriodType } from "../../Enum/EBillingPeriodType"
import { useSubscriptionPlans } from "../../hooks/Company/useSubscriptionPlans"
import { SubscriptionPlanPricing } from "../../Objects/Company/SubscriptionPlanPricing"
import PricingBenefitsPopup from "./PricingBenefitsPopup"
import { trackMarketingEvent } from "../../analytics/tracking"

export const plans = [
  {
    planType: EAccountPlansType.None,
    nameKey: "pricing.plans.essential.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.essential.features.orders_limit", "pricing.plans.essential.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
  {
    planType: EAccountPlansType.Basic,
    nameKey: "pricing.plans.basic.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.basic.features.orders_limit", "pricing.plans.basic.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
  {
    planType: EAccountPlansType.Standard,
    nameKey: "pricing.plans.standard.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.standard.features.orders_limit", "pricing.plans.standard.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: true,
  },
  {
    planType: EAccountPlansType.Pro,
    nameKey: "pricing.plans.pro.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.pro.features.orders_limit", "pricing.plans.pro.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
  {
    planType: EAccountPlansType.Premium,
    nameKey: "pricing.plans.premium.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.premium.features.orders_limit", "pricing.plans.premium.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
  {
    planType: EAccountPlansType.Enterprise,
    nameKey: "pricing.plans.enterprise.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.enterprise.features.orders_limit", "pricing.plans.enterprise.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
  {
    planType: EAccountPlansType.Scale,
    nameKey: "pricing.plans.scale.name",
    featuresKeys: ["pricing.plans.geral.features.same_system", "pricing.plans.scale.features.orders_limit", "pricing.plans.scale.features.products_limit"],
    buttonKey: "pricing.buttons.start",
    highlighted: false,
  },
]

interface PricingProp {
  showButton: boolean
}

export const formatPlatformFeePercent = (platformFeePercent: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(platformFeePercent / 100)

const getPeriodSuffixKey = (periodType: EBillingPeriodType) => {
  switch (periodType) {
    case EBillingPeriodType.Quarterly:
      return "pricing.perQuarter"

    case EBillingPeriodType.Annual:
      return "pricing.perYear"

    default:
      return "pricing.perMonth"
  }
}

const getPeriodMonths = (periodType: EBillingPeriodType) => {
  switch (periodType) {
    case EBillingPeriodType.Quarterly:
      return 3

    case EBillingPeriodType.Annual:
      return 12

    default:
      return 1
  }
}

const Pricing: React.FC<PricingProp> = ({ showButton = true }) => {
  const { t } = useTranslation("login")

  const navigate = useNavigate()

  const { getSubscriptionPlans } = useSubscriptionPlans()

  const [planPricing, setPlanPricing] = useState<SubscriptionPlanPricing[]>([])

  const [selectedPeriodType, setSelectedPeriodType] = useState<EBillingPeriodType>(EBillingPeriodType.Monthly)

  const [isBenefitsPopupOpen, setBenefitsPopupOpen] = useState(false)

  const carouselRef = useRef<HTMLDivElement | null>(null)

  const [activeCardIndex, setActiveCardIndex] = useState(0)

  useEffect(() => {
    getSubscriptionPlans()
      .then((items) => {
        setPlanPricing(items)
      })
      .catch(() => {
        setPlanPricing([])
      })
  }, [getSubscriptionPlans])

  const planPricingMap = useMemo(() => {
    const map = new Map<EAccountPlansType, SubscriptionPlanPricing>()

    planPricing.forEach((item) => {
      map.set(item.planType, item)
    })

    return map
  }, [planPricing])

  const planFeeMap = useMemo(() => {
    const map = new Map<number, number>()

    planPricing.forEach((item) => {
      map.set(item.planType, item.platformFeePercent)
    })

    return map
  }, [planPricing])

  const availablePeriodTypes = useMemo(() => {
    const types = new Set<EBillingPeriodType>()

    planPricing.forEach((item) => {
      item.periods.forEach((period) => types.add(period.periodType))
    })

    if (types.size === 0) {
      types.add(EBillingPeriodType.Monthly)
    }

    return Array.from(types).sort((a, b) => (a as number) - (b as number))
  }, [planPricing])

  const effectivePeriodType = availablePeriodTypes.includes(selectedPeriodType)
    ? selectedPeriodType
    : (availablePeriodTypes[0] ?? EBillingPeriodType.Monthly)

  useEffect(() => {
    const element = carouselRef.current

    if (!element) return

    const onScroll = () => {
      const cards = Array.from(element.children) as HTMLElement[]

      if (cards.length === 0) return

      const viewportCenter = element.scrollLeft + element.clientWidth / 2

      let nearestIndex = 0
      let nearestDistance = Number.POSITIVE_INFINITY

      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.clientWidth / 2
        const distance = Math.abs(cardCenter - viewportCenter)

        if (distance < nearestDistance) {
          nearestDistance = distance
          nearestIndex = index
        }
      })

      setActiveCardIndex(nearestIndex)
    }

    onScroll()
    element.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      element.removeEventListener("scroll", onScroll)
    }
  }, [planPricing.length])

  const scrollToCard = (index: number) => {
    const element = carouselRef.current

    if (!element) return

    const cards = Array.from(element.children) as HTMLElement[]

    const safeIndex = Math.max(0, Math.min(index, cards.length - 1))

    const card = cards[safeIndex]

    if (!card) return

    element.scrollTo({
      left: card.offsetLeft - 8,
      behavior: "smooth",
    })
  }

  return (
    <>
      <Animate type={AnimationTypes.slideUpIn}>
        <div className="max-w-[1280px] mx-auto px-2">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 mb-2">{t("pricing.title")}</h2>

            <p className="text-slate-500">{t("pricing.subtitle")}</p>
          </div>

          {availablePeriodTypes.length > 1 && (
            <div className="mb-6 flex items-center justify-center gap-2">
              {availablePeriodTypes.map((periodType) => (
                <button
                  key={periodType}
                  type="button"
                  onClick={() => setSelectedPeriodType(periodType)}
                  className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                    effectivePeriodType === periodType
                      ? "bg-orange-600 text-white"
                      : "bg-orange-50 text-orange-700 hover:bg-orange-100"
                  }`}
                  data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.selecionarPeriodo"
                >
                  {(periodType as number).toEBillingPeriodTypeToString()}
                </button>
              ))}
            </div>
          )}

          <div className="mb-3 flex items-center justify-end gap-2">
            <button
              type="button"
              aria-label={t("pricing.prev")}
              className="rounded-xl border-2 border-orange-400 bg-orange-50 px-4 py-2 text-orange-700 font-bold shadow-sm transition hover:bg-orange-100"
              onClick={() => {
                carouselRef.current?.scrollBy({ left: -340, behavior: "smooth" })
              }}
              data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.anterior"
            >
              ← {t("pricing.prev")}
            </button>

            <button
              type="button"
              aria-label={t("pricing.next")}
              className="rounded-xl border-2 border-orange-400 bg-orange-600 px-4 py-2 text-white font-bold shadow-md transition hover:bg-orange-700"
              onClick={() => {
                carouselRef.current?.scrollBy({ left: 340, behavior: "smooth" })
              }}
              data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.proximo"
            >
              {t("pricing.next")} →
            </button>
          </div>

          <div
            ref={carouselRef}
            className="flex gap-6 items-stretch overflow-x-auto overflow-y-visible snap-x snap-mandatory pt-4 pb-3 px-1"
          >
            {plans.map((p, i) => {
              const isHighlighted = p.highlighted
              const pricingData = planPricingMap.get(p.planType)

              const feePercent = pricingData?.platformFeePercent ?? 0
              const limitMonthlyOrders = pricingData?.limitMonthlyOrders ?? 0
              const limitProducts = pricingData?.limitProducts ?? 0

              const matchingPeriod = pricingData?.periods.find((period) => period.periodType === effectivePeriodType)
              const hasLegacyMonthlyOnly =
                !pricingData?.periods?.length && effectivePeriodType === EBillingPeriodType.Monthly
              const price = matchingPeriod?.price ?? (hasLegacyMonthlyOnly ? (pricingData?.price ?? 0) : 0)

              const formattedFee = formatPlatformFeePercent(feePercent)
              const formattedPrice = price.toMoney()

              const isFreePlan = p.planType === EAccountPlansType.None
              const isPeriodUnavailable = !isFreePlan && !matchingPeriod && !hasLegacyMonthlyOnly

              const monthlyPeriod = pricingData?.periods.find(
                (period) => period.periodType === EBillingPeriodType.Monthly,
              )
              const monthlyPrice = monthlyPeriod?.price ?? pricingData?.price ?? 0
              const periodMonths = getPeriodMonths(effectivePeriodType)
              const equivalentMonthlyCost = monthlyPrice * periodMonths

              const savingsAmount =
                !isFreePlan && !isPeriodUnavailable && periodMonths > 1 && monthlyPrice > 0
                  ? Math.max(0, equivalentMonthlyCost - price)
                  : 0
              const savingsPercent =
                savingsAmount > 0 ? Math.round((savingsAmount / equivalentMonthlyCost) * 100) : 0
              const hasSavings = savingsAmount > 0 && savingsPercent > 0

              return (
                <div
                  key={i}
                  className={`
                    min-w-[285px] md:min-w-[320px]
                    snap-start
                    relative
                    rounded-2xl
                    border-2
                    transition-all duration-300
                    p-2 md:p-4
                    hover:-translate-y-1
                    hover:shadow-xl
                    ${
                      isHighlighted
                        ? "bg-gradient-to-br from-red-600 to-orange-500 text-white border-transparent shadow-2xl lg:scale-105"
                        : "bg-white border-red-100"
                    }
                  `}
                >
                  {isHighlighted && (
                    <div
                      className="
                        absolute
                        top-1
                        left-1/2 -translate-x-1/2
                        bg-red-700
                        text-white
                        px-4 py-1
                        rounded-full
                        text-[0.6rem]
                        font-extrabold
                        uppercase
                        tracking-widest
                        whitespace-nowrap
                        shadow-md
                      "
                    >
                      {t("pricing.badges.popular")}
                    </div>
                  )}

                  <div className="mb-6 text-center md:text-left">
                    <h3 className="text-lg font-bold mb-1 opacity-90">{t(p.nameKey)}</h3>

                    <div className="flex items-baseline justify-center md:justify-start gap-1">
                      <span className="text-4xl font-black">
                        {isFreePlan ? t("pricing.freePrice") : isPeriodUnavailable ? "—" : formattedPrice}
                      </span>

                      {!isFreePlan && !isPeriodUnavailable && (
                        <span className={`text-xs ${isHighlighted ? "text-orange-200" : "text-slate-500"}`}>
                          {t(getPeriodSuffixKey(effectivePeriodType))}
                        </span>
                      )}
                    </div>

                    {hasSavings && (
                      <span
                        className={`mt-2 inline-block rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${
                          isHighlighted ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {t("pricing.savingsBadge", {
                          percent: `${savingsPercent}%`,
                          amount: savingsAmount.toMoney(),
                        })}
                      </span>
                    )}
                  </div>

                  <ul className="mb-6">
                    {p.featuresKeys.map((key, idx) => (
                      <li key={idx} className="flex items-center gap-3 text-xs font-semibold mb-3">
                        <svg
                          className={`w-4 h-4 flex-shrink-0 ${isHighlighted ? "text-orange-200" : "text-red-600"}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>

                        {t(key, {
                          order: limitMonthlyOrders,
                          product: limitProducts,
                        })}
                      </li>
                    ))}

                    <p
                      className={`mt-3 text-[10px] text-justify font-semibold ${isHighlighted ? "text-orange-100" : "text-gray-600"}`}
                    >
                      {t(isFreePlan ? "pricing.platformFeeFreePlan" : "pricing.platformFeePaidPlan", {
                        fee: formattedFee,
                      })}
                    </p>
                  </ul>

                  <button
                    type="button"
                    onClick={() => {
                      trackMarketingEvent("landing_pricing_click", {
                        cta: "view_benefits",
                        plan: p.planType,
                      })
                      setBenefitsPopupOpen(true)
                    }}
                    className={`mb-4 w-full rounded-xl border px-4 py-3 text-sm font-bold transition-all duration-300 hover:scale-[1.01] ${
                      isHighlighted
                        ? "border-white/30 bg-white/10 text-white hover:bg-white/15"
                        : "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
                    }`}
                    data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.setBenefitsPopupOpenTrue"
                  >
                    {t("pricing.buttons.viewBenefits")}
                  </button>

                  {showButton && (
                    <button
                      onClick={() => {
                        trackMarketingEvent("landing_pricing_click", {
                          cta: "start_plan",
                          plan: p.planType,
                          from: "pricing_section",
                        })
                        navigate(PATH_MAP.ROOT_SIGN_UP)
                      }}
                      className={`
                      w-full
                      py-3.5
                      rounded-xl
                      text-sm font-bold
                      transition-all duration-300
                      hover:scale-105
                      active:scale-95
                      ${
                        isHighlighted
                          ? "bg-white text-red-600 hover:bg-orange-100"
                          : "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100"
                      }
                    `}
                      data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.navigatePATHMAPROOTSIGNUP"
                    >
                      {t(p.buttonKey)}
                    </button>
                  )}
                </div>
              )
            })}
          </div>

          <div className="mt-2 flex items-center justify-center gap-2">
            {plans.map((_, index) => (
              <button
                key={`dot-${index}`}
                type="button"
                aria-label={`Ir para plano ${index + 1}`}
                className={`h-2.5 rounded-full transition-all ${
                  activeCardIndex === index ? "w-7 bg-orange-600" : "w-2.5 bg-orange-200 hover:bg-orange-300"
                }`}
                onClick={() => scrollToCard(index)}
                data-interaction-description-key="interactionDescriptions:components.landPage.pricing.button.irParaPlano"
              />
            ))}
          </div>
        </div>
      </Animate>

      {isBenefitsPopupOpen && <PricingBenefitsPopup onClose={() => setBenefitsPopupOpen(false)} />}
    </>
  )
}

export default Pricing
